// src/pages/AuthCallback.jsx - Handles redirect back from Entra ID SSO
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ERROR_MESSAGES = {
  not_authorized: 'You are not authorized to access this application. Contact your administrator.',
  account_disabled: 'Your account has been disabled. Contact your administrator.',
  auth_failed: 'Authentication was cancelled or failed. Please try again.',
  state_mismatch: 'Security validation failed. Please try signing in again.',
  token_failed: 'Failed to complete sign-in with Microsoft. Please try again.',
  profile_failed: 'Failed to retrieve your profile from Microsoft. Please try again.',
  group_check_failed: 'Failed to verify your group membership. Please try again.',
};

const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loadUser } = useAuth();
  const [error, setError] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    const refreshToken = searchParams.get('refresh_token');
    const err = searchParams.get('error');

    if (err) {
      setError(ERROR_MESSAGES[err] || 'An unexpected error occurred during sign-in.');
      return;
    }

    if (token && refreshToken) {
      localStorage.setItem('access_token', token);
      localStorage.setItem('refresh_token', refreshToken);
      loadUser().then(() => navigate('/', { replace: true }));
    } else {
      setError('Invalid callback. Please try signing in again.');
    }
  }, []);

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #6B3FA0 0%, #4A2B70 100%)',
        padding: '20px',
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(74, 43, 112, 0.3)',
          padding: '48px 40px',
          maxWidth: '450px',
          width: '100%',
          textAlign: 'center',
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: '#FEE2E2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: '28px',
          }}>
            ✕
          </div>
          <h2 style={{ color: '#DC2626', marginBottom: '12px', fontSize: '22px' }}>
            Sign-In Failed
          </h2>
          <p style={{ color: '#555', marginBottom: '28px', lineHeight: '1.5' }}>
            {error}
          </p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            style={{
              padding: '12px 28px',
              backgroundColor: '#6B3FA0',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #6B3FA0 0%, #4A2B70 100%)',
    }}>
      <div style={{ color: 'white', textAlign: 'center' }}>
        <div style={{
          width: '44px',
          height: '44px',
          border: '3px solid rgba(255,255,255,0.3)',
          borderTopColor: 'white',
          borderRadius: '50%',
          animation: 'spin 0.7s linear infinite',
          margin: '0 auto 16px',
        }} />
        <p style={{ fontSize: '18px', margin: 0 }}>Completing sign-in...</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AuthCallback;
